import React, { useState, useEffect, useMemo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { translate as translateConstructor, Spinner } from '@haldor/ui';
import { Form, FormSpy } from 'react-final-form';
import { Box, Typography, Paper, Button, Alert } from '@mui/material';
import { getFormTemplatesByType } from 'actions/templates';
import FormFieldRenderer from 'components/FormFieldRenderer';
import { FormPrompt } from 'components/Inputs/FormPrompt';
import './DisplayForm.scss';

const DisplayForm = ({ type, fields: providedFields, onSubmit, initialValues = [], onSuccess, onCancel, hideSaveButton = false }) => {
	const [loading, setLoading] = useState(true);
	const [submitting, setSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState(null);
	const dispatch = useDispatch();

	const allTemplatesByType = useSelector(state => state.Templates.templatesByType || {});
	const translations = useSelector(state => state.Languages.translations);
	const translate = translateConstructor(translations);

	// Pre-compute existing field results for efficient lookups when updating
	const existingResultsByField = useMemo(() => {
		const map = {};

		// Handle initialValues as either array of results or object with field values
		if (Array.isArray(initialValues)) {
			initialValues.forEach(result => {
				if (result && result.fieldName) {
					map[result.fieldName] = result;
				}
			});
		} else if (initialValues && typeof initialValues === 'object') {
			// If initialValues is an object { fieldName: value }, convert to result format
			Object.keys(initialValues).forEach(fieldName => {
				map[fieldName] = {
					fieldName,
					value: initialValues[fieldName]
				};
			});
		}

		return map;
	}, [initialValues]);

	// Convert initialValues to object format for React Final Form
	const formInitialValues = useMemo(() => {
		if (Array.isArray(initialValues)) {
			// Convert array of results to object { fieldName: value }
			const values = {};
			initialValues.forEach(result => {
				if (result && result.fieldName) {
					values[result.fieldName] = result.value;
				}
			});
			return values;
		} else if (initialValues && typeof initialValues === 'object') {
			// Already in object format
			return initialValues;
		}
		return {};
	}, [initialValues]);

	// Prepare templatesByType depending on what was provided
	// Accept either an array of fields OR an object { id, fields: [...] }
	let templatesByType;
	if (providedFields) {
		if (Array.isArray(providedFields)) {
			templatesByType = { fields: providedFields, id: null };
		} else if (Array.isArray(providedFields.fields)) {
			templatesByType = { fields: providedFields.fields, id: providedFields.id || null };
		} else {
			templatesByType = { fields: [], id: null };
		}
	} else {
		templatesByType = allTemplatesByType[type];
	}

	useEffect(() => {

		const fetchTemplates = async () => {
			// Only fetch templates if fields prop is not provided and type is specified
			if (!providedFields && type && !allTemplatesByType[type]) {
				setLoading(true);
				await dispatch(getFormTemplatesByType(type));
				setLoading(false);
			} else {
				setLoading(false);
			}
		};

		fetchTemplates();
	}, [type, providedFields, dispatch, allTemplatesByType]);

	const handleSubmit = (values) => {
		return new Promise((resolve) => {
			setSubmitting(true);
			setSubmitError(null); // Clear previous errors

			// Collect field results similar to FormInstance
			let fieldResults = [];

			Object.keys(values).forEach((key) => {
				const templateField = fields.find(t => t.name === key);
				const existingResult = existingResultsByField[key];

				let result = {
					fieldName: key,
					value: values[key],
					fieldTemplateId: templateField != null ? templateField.id : null,
				};

				if (existingResult?.id != null) {
					result.id = existingResult.id;
				}

				fieldResults.push(result);
			});

			const data = {
				fieldResults,
				status: 'SUBMITTED',
				formTemplateId: templatesByType?.id || null,
				type: type || null,
			};

			// Call onSubmit if provided
			if (onSubmit) {
				onSubmit(data)
					.then(() => {
						setSubmitting(false);
						setSubmitError(null);

						// Call onSuccess callback if provided (for closing modals, etc.)
						if (onSuccess) {
							setTimeout(() => {
								onSuccess({ success: true });
							}, 1000); // Delay to allow user to see success message
						}

						resolve();
					})
					.catch((error) => {
						setSubmitting(false);
						const errorMessage = error?.response?.data?.message ||
							error?.message ||
							'Could not submit form';
						setSubmitError(errorMessage);
						resolve({ error: errorMessage });
					});
			} else {
				setSubmitting(false);
				resolve();
			}
		});
	};

	if (loading) {
		return <Spinner center />;
	}

	// No template found and no fields provided
	if (!templatesByType && !providedFields) {
		return (
			<Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', p: 3 }}>
				<Typography variant="body1" color="text.secondary">
					{translate('No form templates found for this type')}
				</Typography>
			</Box>
		);
	}

	// Resolve raw fields depending on provided shape
	const rawFields = providedFields
		? (Array.isArray(providedFields) ? providedFields : (providedFields.fields || []))
		: (templatesByType.fields || templatesByType.FieldTemplates || []);

	// Sort fields by sortOrder - use fields prop if provided, otherwise from template
	const fields = [...rawFields];
	fields.sort((a, b) => ((a.sortOrder || 0) > (b.sortOrder || 0) ? 1 : -1));

	// Ensure each field has a name property
	fields.forEach((field, index) => {
		if (!field.name) {
			field.name = field.fieldName || `field-${index}`;
		}
	});

	return (
		<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
			<Form
				onSubmit={handleSubmit}
				initialValues={formInitialValues}
				render={({ handleSubmit, form, submitSucceeded, modifiedSinceLastSubmit }) => (
					<form onSubmit={handleSubmit}>
						<FormPrompt
							hasUnsavedChanges={(form.getState().dirty && !form.getState().submitSucceeded) || form.getState().dirtySinceLastSubmit}
							formPromptTitle={translate('are-you-sure')}
							formPromptMessage={translate('forminstance-error-leave-message')}
							formPromptCancel={translate('Cancel')}
							formPromptOk={translate('Ok')}
							currentPathContext={window.location.pathname}
						/>

						{/* Display error message if submission failed */}
						{submitError && (
							<Alert severity="error" onClose={() => setSubmitError(null)} sx={{ mb: 2 }}>
								{submitError}
							</Alert>
						)}

						<Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
							{fields.map((field, index) => (
								<FormFieldRenderer
									key={index}
									field={field}
									index={index}
									translate={translate}
								/>
							))}
						</Box>

						{/* Submit and Cancel buttons */}
						{!hideSaveButton && (
							<Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 3, justifyContent: 'space-between' }}>
								<Button
									type="submit"
									variant="contained"
									color="primary"
									disabled={submitting}
									sx={{ textTransform: 'none' }}
								>
									{submitting ? translate('Submitting') : translate('Send')}
								</Button>
								{onCancel && (
									<Button
										type="button"
										variant="outlined"
										onClick={onCancel}
										disabled={submitting}
										sx={{ textTransform: 'none' }}
									>
										{translate('Cancel')}
									</Button>
								)}

								<FormSpy subscription={{ submitSucceeded: true, modifiedSinceLastSubmit: true, submitError: true }}>
									{({ submitSucceeded, modifiedSinceLastSubmit, submitError: formError }) => (
										<>
											{submitSucceeded && !modifiedSinceLastSubmit && !submitError ? (
												<Typography variant="body2" color="success.main" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
													{translate('saved')}
												</Typography>
											) : null}
										</>
									)}
								</FormSpy>
							</Box>
						)}
					</form>
				)}
			/>
		</Box>
	);
};

export default DisplayForm;
